import React, { useEffect, useState } from 'react';
import {
  FormatAlignCenter,
  FormatAlignJustify,
  FormatAlignLeft,
  FormatAlignRight,
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  FormatStrikethrough,
  FormatUnderlined,
  HorizontalRule,
  Redo,
  Undo,
  Link as Hyperlink,
} from '@mui/icons-material';
import { Box, ButtonGroup, ToggleButton, ToggleButtonProps, Select, MenuItem, SelectChangeEvent } from '@mui/material';
import { ChainedCommands, useCurrentEditor } from '@tiptap/react';
import './RichTextEditor.scss';
import HyperlinkPopover from '../popover/hyperlinkPopover/HyperlinkPopover';

export default function MenuBar() {
  const { editor } = useCurrentEditor();
  
  const [hyperlinkElement, setHyperlinkElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (editor) {
      const onUpdate = () => {
        const currentAttributes = editor.getAttributes('textStyle');
        if (!currentAttributes.fontSize) {
          applyFontSize('12');
        }
      };

      editor.on('update', onUpdate);

      return () => {
        editor.off('update', onUpdate);
      };
    }
  }, [editor]);

  if (!editor) return null;

  const FONT_SIZES = Array.from({ length: 40 }, (_, i) => (i + 6).toString());

  const buttonProps = (action: (chain: ChainedCommands) => ChainedCommands, checkName?: string, checkAttributes?: object): ToggleButtonProps => {
    return {
      selected: !!(checkName && editor.isActive(checkName, checkAttributes)),
      onChange: () => action(editor.chain().focus()).run(),
      disabled: !action(editor.can().chain().focus()).run(),
      value: '',
      size: 'small',
    };
  };

  const hyperlinkButtonProps = (): ToggleButtonProps => {
    return {
      selected: editor.isActive('link'),
      onClick: handleHyperlinkButtonClick,
      value: '',
      size: 'small',
    };
  };

  const getCurrentFontSize = (): number => {
    const fontSizeAttr = editor.getAttributes('textStyle')?.fontSize;
    if (fontSizeAttr) {
      return Math.round(parseFloat(fontSizeAttr) * 12);
    }
    return 12;
  };

  const applyFontSize = (size: string): void => {
    const remSize = (parseInt(size, 10) / 12).toFixed(2) + 'rem';
    editor.chain().focus().setMark('textStyle', { fontSize: remSize }).run();
  };

  const handleFontSizeChange = (event: SelectChangeEvent) => {
    applyFontSize(event.target.value);
  };

  const handleHyperlinkButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    setHyperlinkElement(event.currentTarget);
  };

  const handleHyperlinkPopoverClose = () => {
    setHyperlinkElement(null);
  };

  return (
    <Box className="MenuBar" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
      <ButtonGroup sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        <ToggleButton {...buttonProps((chain) => chain.toggleBold(), 'bold')}>
          <FormatBold />
        </ToggleButton>
        <ToggleButton {...buttonProps((chain) => chain.toggleItalic(), 'italic')}>
          <FormatItalic />
        </ToggleButton>
        <ToggleButton {...buttonProps((chain) => chain.toggleStrike(), 'strike')}>
          <FormatStrikethrough />
        </ToggleButton>
        <ToggleButton {...buttonProps((chain) => chain.toggleUnderline(), 'underline')}>
          <FormatUnderlined />
        </ToggleButton>
        <ToggleButton {...buttonProps((chain) => chain.toggleBulletList(), 'bulletList')}>
          <FormatListBulleted />
        </ToggleButton>
        <ToggleButton {...buttonProps((chain) => chain.toggleOrderedList(), 'orderedList')}>
          <FormatListNumbered />
        </ToggleButton>
        <ToggleButton {...buttonProps((chain) => chain.toggleBlockquote(), 'blockquote')}>
          <FormatQuote />
        </ToggleButton>
        <ToggleButton {...buttonProps((chain) => chain.setTextAlign('left'), 'textAlign', { textAlign: 'left' })}>
          <FormatAlignLeft />
        </ToggleButton>
        <ToggleButton {...buttonProps((chain) => chain.setTextAlign('center'), 'textAlign', { textAlign: 'center' })}>
          <FormatAlignCenter />
        </ToggleButton>
        <ToggleButton {...buttonProps((chain) => chain.setTextAlign('right'), 'textAlign', { textAlign: 'right' })}>
          <FormatAlignRight />
        </ToggleButton>
        <ToggleButton {...buttonProps((chain) => chain.setTextAlign('justify'), 'textAlign', { textAlign: 'justify' })}>
          <FormatAlignJustify />
        </ToggleButton>
        <ToggleButton {...buttonProps((chain) => chain.setHorizontalRule())}>
          <HorizontalRule />
        </ToggleButton>
        <ToggleButton {...buttonProps((chain) => chain.undo())}>
          <Undo />
        </ToggleButton>
        <ToggleButton {...buttonProps((chain) => chain.redo())}>
          <Redo />
        </ToggleButton>
        <ToggleButton {...hyperlinkButtonProps()}>
          <Hyperlink />
        </ToggleButton>
      </ButtonGroup>
      <Select
        value={getCurrentFontSize().toString()}
        onChange={handleFontSizeChange}
        size="small"
        displayEmpty
        MenuProps={{
          PaperProps: { className: 'CustomMenu' },
        }}
      >
        {FONT_SIZES.map((size) => (
          <MenuItem key={size} value={size}>
            {size}
          </MenuItem>
        ))}
      </Select>
      <HyperlinkPopover
        hyperlinkElement={hyperlinkElement}
        onPopoverClose={handleHyperlinkPopoverClose}
      />
    </Box>
  );
}
